import React, { useState, useRef, useEffect, useMemo, useCallback } from "react";
import PropTypes from "prop-types";
import {
  TextField,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Box,
  Chip,
} from "@mui/material";
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';

const formatCurrency = (value) =>
  new Intl.NumberFormat("es-CL", { style: "currency", currency: "CLP", minimumFractionDigits: 2 }).format(value);
const formatPercentage = (ratio) =>
  new Intl.NumberFormat("es-CL", { style: "percent", minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(ratio / 100);

export default function ApelacionTabla({ data = {}, onChange }) {
  const [openZones, setOpenZones] = useState({});
  const initialDifferencesRef = useRef({});

  // Inyecta initialCount en cada fila la primera vez
  useEffect(() => {
    let needsInit = false;
    for (const rows of Object.values(data)) {
      if (rows.length > 0 && rows[0].initialCount == null) {
        needsInit = true;
        break;
      }
    }
    if (!needsInit) return;

    const enriched = {};
    Object.entries(data).forEach(([period, rows]) => {
      // Cálculo de recuento por zona
      const counts = rows.reduce((acc, row) => {
        const z = String(row.zona);
        acc[z] = (acc[z] || 0) + 1;
        return acc;
      }, {});

      enriched[period] = rows.map((row) => ({
        ...row,
        initialCount: counts[String(row.zona)],
      }));
    });

    onChange(enriched);
  }, [data, onChange]);

  // Chip color según porcentaje
  const getChipColor = useCallback((percent) => {
    const ratio = Math.min(Math.max(percent, 0), 100) / 100;
    const r = Math.round(200 + 55 * ratio);
    const g = Math.round(200 * (1 - ratio));
    const b = Math.round(200 * (1 - ratio));
    return `rgb(${r},${g},${b})`;
  }, []);

  // Agrupa y ordena datos
  const memoizedData = useMemo(() => {
    return Object.entries(data).map(([period, rows]) => {
      // Guarda total inicial de diferencias
      if (initialDifferencesRef.current[period] == null) {
        initialDifferencesRef.current[period] = rows.reduce((sum, r) => sum + r.diferencia, 0);
      }

      // Agrupación por zona
      const groups = rows.reduce((acc, row) => {
        const z = String(row.zona);
        acc[z] = acc[z] || [];
        acc[z].push(row);
        return acc;
      }, {});

      const sortedZones = Object.entries(groups)
        .map(([zona, grp]) => {
          const totalDif = grp.reduce((sum, r) => sum + r.diferencia, 0);
          const initial = grp[0].initialCount;

          // Ordena filas y añade meta
          const sortedRows = grp.slice().sort((a, b) => a.diferencia - b.diferencia);
          const rowsWithMeta = sortedRows.map((row) => {
            const diffPercent = Math.min(Math.max(row.d_total_sagec - row.d_total_operador, 0), 100);
            return {
              ...row,
              diffPercent,
              chipColor: getChipColor(diffPercent),
            };
          });

          return { zona, totalDif, initial, rows: rowsWithMeta };
        })
        .sort((a, b) => Number(a.zona) - Number(b.zona));

      return { period, zones: sortedZones };
    });
  }, [data, getChipColor]);

  const toggleZone = (zona) =>
    setOpenZones((prev) => ({ ...prev, [zona]: !prev[zona] }));

  const handleDelete = (period, rbdToDelete) => {
    const updated = { ...data };
    updated[period] = updated[period].filter((row) => row.rbd !== rbdToDelete);
    onChange(updated);
  };

  const handleDeleteZone = (period, zonaToDelete) => {
    const updated = { ...data };
    updated[period] = updated[period].filter((row) => String(row.zona) !== String(zonaToDelete));
    onChange(updated);
  };

  const handleDescription = (period, rbdToUpdate, value) => {
    const updated = { ...data };
    updated[period] = updated[period].map((row) =>
      row.rbd === rbdToUpdate ? { ...row, descripcion: value } : row
    );
    onChange(updated);
  };

  return (
    <>
      {memoizedData.map(({ period, zones }) => {
        const initialTotal = initialDifferencesRef.current[period] || 0;
        const currentTotal = zones.reduce((sum, z) => sum + z.totalDif, 0);

        return (
          <Box key={period} sx={{ mb: 4 }}>
            <Typography variant="h4" sx={{ mb: 2 }}>Diferencias en cálculo de prefactura</Typography>


            <TableContainer component={Paper}>
              <Table>
                <TableBody>
                  {zones.map(({ zona, totalDif, initial, rows }) => (
                    <React.Fragment key={zona}>
                      <TableRow>
                        <TableCell colSpan={6} sx={{ background: '#f5f5f5', p: 0 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, py: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <IconButton size="small" onClick={() => toggleZone(zona)}>
                                {openZones[zona] ? <KeyboardArrowDownIcon /> : <KeyboardArrowRightIcon />}
                              </IconButton>
                              <Typography sx={{ ml: 1 }}>
                                Zona {zona} — Total desc.: {formatCurrency(totalDif)} — Apelados {rows.length} de {initial}
                              </Typography>
                            </Box>
                            <IconButton size="small" onClick={() => handleDeleteZone(period, zona)}>
                              <DeleteForeverIcon />
                            </IconButton>
                          </Box>
                        </TableCell>
                      </TableRow>

                      {openZones[zona] ? (
                        <> {/* expanded */}
                          <TableRow>
                            <TableCell />
                            <TableCell>RBD</TableCell>
                            <TableCell>Descuento Sagec</TableCell>
                            <TableCell>Descuento Operador</TableCell>
                            <TableCell>Diferencia</TableCell>
                            <TableCell>Descripción</TableCell>
                          </TableRow>
                          {rows.map((row) => (
                            <TableRow key={`${zona}-${row.rbd}`}>  
                              <TableCell sx={{ py: 0 }}>
                                <IconButton onClick={() => handleDelete(period, row.rbd)}>
                                  <RemoveCircleOutlineIcon />
                                </IconButton>
                              </TableCell>
                              <TableCell>{row.rbd}</TableCell>
                              <TableCell>{formatPercentage(row.d_total_sagec)}</TableCell>
                              <TableCell>{formatPercentage(row.d_total_operador)}</TableCell>
                              <TableCell>{formatCurrency(row.diferencia)}</TableCell>
                              <TableCell sx={{ minWidth: 300 }}>
                                <TextField
                                  fullWidth
                                  multiline
                                  minRows={1}
                                  value={row.descripcion}
                                  onChange={(e) => handleDescription(period, row.rbd, e.target.value)}
                                />
                              </TableCell>
                            </TableRow>
                          ))}
                        </>
                      ) : (
                        <TableRow>
                          <TableCell colSpan={6} sx={{ py: 1, pl: 4 }}>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                              {rows.map((row) => (
                                <Chip
                                  key={row.rbd}
                                  label={`${row.rbd} (${formatCurrency(row.diferencia)})`}
                                  size="small"
                                  onDelete={() => handleDelete(period, row.rbd)}
                                  deleteIcon={<RemoveCircleOutlineIcon fontSize="small" />}
                                  sx={{ backgroundColor: row.chipColor, color: '#fff' }}
                                />
                              ))}
                            </Box>
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        );
      })}
    </>
  );
}

ApelacionTabla.propTypes = {
  data: PropTypes.object.isRequired,
  onChange: PropTypes.func.isRequired,
};
